import React, { useEffect, useState } from "react";
import "./EzERP.css";
import { SlideShell } from "../../shared/components/SlideShell";
import { DeckNavigation } from "../../shared/components/DeckNavigation";
import { useSlideObserver } from "../../hooks/useSlideObserver";
import { TranslationProvider, useTranslation } from "../../shared/contexts/TranslationContext";
import { LanguageToggle } from "../../shared/components/LanguageToggle";
import { AdminLink } from "../../shared/components/AdminLink";

// Slides
import { Slide1, Slide2, Slide3, Slide4, Slide5, Slide6, Slide7, Slide8 } from "./components/EzERPSlides";

const slides = [
  <Slide1 />,
  <Slide2 />,
  <Slide3 />,
  <Slide4 />,
  <Slide5 />,
  <Slide6 />,
  <Slide7 />,
  <Slide8 />
];

export const EzERPDeckInner = ({ lang, setLang }) => {
  const [active, setActive] = useState(0);
  const deckRef = React.useRef(null);
  
  const { translations } = useTranslation();
  const t = translations && translations.s1 ? translations : { s1: {}, s2: {}, s3: {}, s4: {}, s5: {}, s6: {}, s7: {}, s8: {} };

  const scrollTo = (idx) => {
    const el = deckRef.current?.querySelector(`[data-slide-index="${idx}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    localStorage.setItem("ezerp_lang", lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  if (!mounted) return null;

  return (
    <div className="theme-ezerp min-h-screen bg-[var(--bg)] text-[var(--text)] font-sans antialiased selection:bg-[var(--copper)] selection:text-black">

      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-[rgba(11,11,12,0.72)] backdrop-blur-md border-b border-[var(--line)]">
        <div className="max-w-[1400px] w-full mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 font-serif font-bold tracking-wide">
            <span className="w-2.5 h-2.5 rounded-full bg-[var(--copper)] shadow-[0_0_0_4px_rgba(200,122,60,0.18)]"></span>
            EzERP
          </div>
          <nav className="hidden md:flex gap-7 text-[13px] text-[var(--muted)] pr-[200px] pointer-events-none">
            <button onClick={() => scrollTo(1)} className="pointer-events-auto hover:text-[var(--copper-2)] transition-colors">Platform</button>
            <button onClick={() => scrollTo(2)} className="pointer-events-auto hover:text-[var(--copper-2)] transition-colors">Team</button>
            <button onClick={() => scrollTo(3)} className="pointer-events-auto hover:text-[var(--copper-2)] transition-colors">Go-to-Market</button>
            <button onClick={() => scrollTo(5)} className="pointer-events-auto hover:text-[var(--copper-2)] transition-colors">Financials</button>
            <button onClick={() => scrollTo(6)} className="pointer-events-auto hover:text-[var(--copper-2)] transition-colors">Market</button>
          </nav>
        </div>

        {/* Absolute Right Controls */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-4">
          <LanguageToggle lang={lang} setLang={setLang} className="relative z-[60]" />
          <AdminLink className="relative z-[60] group" />
        </div>
      </div>

      <DeckNavigation count={slides.length} active={active} onJump={scrollTo} variant="bars" />

      <main ref={deckRef} className="deck no-scrollbar">
        <SlideShell index={0} theme="ezerp" onActive={setActive}><Slide1 t={t.s1} /></SlideShell>
        <SlideShell index={1} theme="ezerp" onActive={setActive}><Slide2 t={t.s2} /></SlideShell>
        <SlideShell index={2} theme="ezerp" onActive={setActive}><Slide3 t={t.s3} /></SlideShell>
        <SlideShell index={3} theme="ezerp" onActive={setActive}><Slide4 t={t.s4} /></SlideShell>
        <SlideShell index={4} theme="ezerp" onActive={setActive}><Slide5 t={t.s5} /></SlideShell>
        <SlideShell index={5} theme="ezerp" onActive={setActive}><Slide6 t={t.s6} lang={lang} /></SlideShell>
        <SlideShell index={6} theme="ezerp" onActive={setActive}><Slide7 t={t.s7} /></SlideShell>
        <SlideShell index={7} theme="ezerp" onActive={setActive}><Slide8 t={t.s8} lang={lang} /></SlideShell>
      </main>

      <footer className="py-9 pb-12 text-[var(--dim)] text-[13px] text-center border-t border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-8">
          © EzERP · Built for Control. Powered for Growth.
        </div>
      </footer>
    </div>
  );
};

export const EzERPDeck = () => {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("ezerp_lang");
      if (stored === "pt" || stored === "en" || stored === "es") return stored;
      const nav = (navigator.language || "en").toLowerCase();
      if (nav.startsWith("es")) return "es";
      return nav.startsWith("pt") ? "pt" : "en";
    }
    return "en";
  });

  return (
    <TranslationProvider deck="ezerp" lang={lang}>
      <EzERPDeckInner lang={lang} setLang={setLang} />
    </TranslationProvider>
  );
};

export default EzERPDeck;

